import { useSyncExternalStore } from 'react';
import type { ResizeAxis } from '../state/itemResize';
import type { OpeningEdge } from '../state/openingResize';
import type { OpeningDimension } from '../render/three/Viewer';
import { t } from '../i18n/I18n';

/**
 * The size of the item in resize mode that the pointer, a finger or the keyboard is on: a handle
 * in 3D or on the plan, or a size in the resize bar. Everything that stands for that size lights
 * up together — the handle, the face it moves, its readout — so they read as one control.
 * Screen state only: nothing here reaches the document or the history.
 */
let hot: ResizeAxis | null = null;
const listeners = new Set<() => void>();

export function setResizeHot(axis: ResizeAxis | null): void {
  if (axis === hot) return;
  hot = axis;
  for (const listener of listeners) listener();
}
/** Clears the hot size only while it is still this one: leaving a control must not undo entering the next. */
export function releaseResizeHot(axis: ResizeAxis): void { if (hot === axis) setResizeHot(null); }
export function resizeHot(): ResizeAxis | null { return hot; }
export function subscribeResizeHot(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}
export function useResizeHot(): ResizeAxis | null { return useSyncExternalStore(subscribeResizeHot, resizeHot, resizeHot); }

// Read when shown, in the interface's language: the getters keep the tables' shape for their readers.
export const RESIZE_AXIS_LETTER: Record<ResizeAxis, string> = { get w() { return t('#EditorSizeLetterWidth'); }, get d() { return t('#EditorSizeLetterDepth'); }, get h() { return t('#EditorSizeLetterHeight'); } };
export const RESIZE_AXIS_NAME: Record<ResizeAxis, string> = { get w() { return t('Width'); }, get d() { return t('Depth'); }, get h() { return t('Height'); } };

/** An opening's resize mode by its edges: what each handle is called, and its sizes as the inspector names them. */
export const OPENING_EDGE_NAME: Record<OpeningEdge, string> = {
  get left() { return t('#EditorEdgeLeft'); }, get right() { return t('#EditorEdgeRight'); }, get top() { return t('#EditorEdgeTop'); }, get bottom() { return t('#EditorEdgeBottom'); },
  get 'top-left'() { return t('#EditorCornerTopLeft'); }, get 'top-right'() { return t('#EditorCornerTopRight'); }, get 'bottom-left'() { return t('#EditorCornerBottomLeft'); }, get 'bottom-right'() { return t('#EditorCornerBottomRight'); },
};
export const OPENING_DIMENSION_NAME: Record<OpeningDimension, string> = { get width() { return t('Width'); }, get height() { return t('Height'); }, get sill() { return t('#EditorSillHeight'); } };
export const OPENING_DIMENSION_LETTER: Record<OpeningDimension, string> = { get width() { return t('#EditorSizeLetterWidth'); }, get height() { return t('#EditorSizeLetterHeight'); }, get sill() { return t('#EditorSillLetter'); } };
